import { ref, watch, nextTick, onBeforeUnmount } from 'vue'

export type UsePopUpParams = {
  buttonSelector: string
  popUpSelector: string
  persistent?: boolean
}

export const usePopUp = (params: UsePopUpParams) => {
  const isVisible = ref(false)
  const originFocusedElement = ref<Element | null>(null)

  // コンテンツ外をクリックした際に非表示にする
  const handleClickOutside = (event: MouseEvent) => {
    if (params.persistent) return
    const target = event.target

    if (!target || !(target instanceof Element) || target.closest(params.popUpSelector) || target.closest(params.buttonSelector)) return
    isVisible.value = false
  }

  // キーボード操作対応
  const handleKeydown = (event: KeyboardEvent) => {
    if (!params.persistent && event.key === 'Escape') {
      isVisible.value = false
    }
  }

  // コンテンツを表示中はコンテンツ外にフォーカスが移動しないようにする
  const updateFocusableElements = (visible: boolean) => {
    const elements = document.querySelectorAll('a, button, input, select, textarea, details, [tabindex]')
    elements.forEach((element) => {
      if (element.closest(params.popUpSelector) || element.closest(params.buttonSelector)) return
      if (visible) {
        const originalTabIndex = element.getAttribute('tabindex')
        if (originalTabIndex) element.setAttribute('data-tabindex', originalTabIndex)
        element.setAttribute('tabindex', '-1')
      } else {
        const originalTabIndex = element.getAttribute('data-tabindex')
        if (originalTabIndex) {
          element.setAttribute('tabindex', originalTabIndex)
          element.removeAttribute('data-tabindex')
        } else {
          element.removeAttribute('tabindex')
        }
      }
    })
  }

  watch(isVisible, (newValue) => {
    updateFocusableElements(newValue)

    if (newValue) {
      originFocusedElement.value = document.activeElement
      window.addEventListener('click', handleClickOutside)
      window.addEventListener('keydown', handleKeydown)

      nextTick(() => {
        const content = document.querySelector(params.popUpSelector)
        if (!(document.activeElement instanceof HTMLInputElement) && content instanceof HTMLDivElement) {
          content.focus()
        }
      })
    } else {
      window.removeEventListener('click', handleClickOutside)
      window.removeEventListener('keydown', handleKeydown)

      nextTick(() => {
        if (originFocusedElement.value instanceof HTMLButtonElement || originFocusedElement.value instanceof HTMLDivElement) {
          originFocusedElement.value.focus()
        }
        originFocusedElement.value = null
      })
    }
  })

  onBeforeUnmount(() => {
    window.removeEventListener('click', handleClickOutside)
    window.removeEventListener('keydown', handleKeydown)
  })

  return {
    isVisible,
    handleClickOutside,
    handleKeydown,
    updateFocusableElements,
  }
}
